import React, { useState, useEffect } from "react";
import TableSkeleton from "../../../../../component/TableSkeleton";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import FormField from "../../../../../component/Formfield";
import EmptyState from "../../../../../component/EmptyState";
import { headCellSx, bodyCellSx } from "../../../../../component/listing";
import { getProjectReport } from "../../../../../services/timesheet";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens } from "../../../../../theme";

const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const monthRange = () => {
  const d = new Date();
  const from = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
  const to = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
  return { from, to };
};

const ProjectReport = () => {
  const notify = useNotify();
  const [{ from, to }, setRange] = useState(monthRange());
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = async () => {
    setLoading(true); setError(false);
    try {
      const res = await getProjectReport({ from, to, status: status || undefined });
      if (res?.success === false) throw new Error(res?.message || "Failed to load report");
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load report"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [from, to, status]);

  const totals = rows.reduce((a, r) => ({ hours: a.hours + (r.hours || 0), bill: a.bill + (r.billableAmount || 0) }), { hours: 0, bill: 0 });

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Project Report"} breadcrumbs={[{ label: "Timesheet", href: "/app/timesheet" }, { label: "Project Report" }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" spacing={1.5} sx={{ mb: "1.25rem" }} flexWrap="wrap">
          <FormField type="date" label="From" value={from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
          <FormField type="date" label="To" value={to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
          <FormField type="select" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            options={[{ name: "All", value: "" }, { name: "Pending", value: "pending" }, { name: "Approved", value: "approved" }]} />
        </Stack>

        {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Project</TableCell>
                <TableCell sx={headCellSx} align="right">Hours</TableCell>
                <TableCell sx={headCellSx} align="right">Billable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody><TableSkeleton columns={3} /></TableBody>
          </Table>
        ) : error ? (
          <EmptyState variant="error" title="Couldn't load report" onRetry={fetchData} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No hours logged in this range"
            /* The range is the usual reason this is empty, and it defaults to
               the current month — so a report opened on the 1st, or over data
               from an earlier period, reads as broken unless it says which
               dates it is looking at. */
            description={`Nothing between ${from} and ${to}${status ? ` with status "${status}"` : ""}. Widen the dates above to look further back.`}
          />
        ) : (
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Project</TableCell>
                <TableCell sx={headCellSx} align="right">Hours</TableCell>
                <TableCell sx={headCellSx} align="right">Billable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.project}</TableCell>
                  <TableCell sx={bodyCellSx} align="right">{r.hours}</TableCell>
                  <TableCell sx={bodyCellSx} align="right">{money(r.billableAmount)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>Total</TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">{totals.hours}</TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">{money(totals.bill)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </MainBox>
    </Box>
  );
};

export default ProjectReport;
